import React from "react";
import {
  Calendar,
  CheckCircle,
  Clock,
  Eye,
  XCircle,
  AlertCircle,
  CircleAlert,
} from "lucide-react";
import { RegularizationType } from "../../../types/regularization.types";
import {
  getWorkflowStatusFallbackLabel,
  isApprovedWorkflowStatus,
  WorkflowStatusCode,
} from "../../../utils/constants";
import { formatISODate } from "../../../utils/timeUtils";
import { TableColumn } from "../../common/Table";
import Badge from "../../common/Badge";
import { getWorkflowStatusVariant } from "../../../utils/badgeVariants";
import {
  SimpleTooltip,
  IconActionButton,
  EmployeeCell,
  ApproveButton,
  RejectButton,
} from "../../common";
import { getAvatarGradient } from "../../../utils/avatarUtils";
import { IRegularizationRequest } from "../../../store/apis/attendanceRegularization.api";

interface BaseColumnConfig {
  formatDate: (date: string) => string;
  formatTime: (time: string) => string;
  getRegularizationTypeLabel: (type: RegularizationType) => string;
  getStatusColor: (status: WorkflowStatusCode) => string;
  handleViewDetails: (request: IRegularizationRequest) => void;
}

interface PendingColumnConfig extends BaseColumnConfig {
  handleApproveClick: (request: IRegularizationRequest) => void;
  handleRejectClick: (request: IRegularizationRequest) => void;
  canActOnRequest: (request: IRegularizationRequest) => boolean;
  processing: string | null;
}

export const getWorkflowStatusIcon = (status?: string) => {
  if (!status) return CircleAlert;

  const normalized = status.toLowerCase();

  if (normalized.startsWith("pending")) return Clock;
  if (normalized.startsWith("approved")) return CheckCircle;
  if (normalized.startsWith("rejected")) return XCircle;
  if (normalized === "cancelled") return AlertCircle;

  return CircleAlert;
};

export const getPendingColumns = (
  config: PendingColumnConfig,
): TableColumn<IRegularizationRequest>[] => {
  const {
    formatDate,
    formatTime,
    handleViewDetails,
    handleApproveClick,
    handleRejectClick,
    canActOnRequest,
    processing,
  } = config;

  return [
    {
      key: "employee",
      title: "Employee",
      label: "Employee",
      required: true,
      render: (_, r) => (
        <EmployeeCell
          name={r.fullName || "-"}
          sub={`${r.employeeId || ""} • ${r.departmentName || r.department || ""}`}
          avatarBg={getAvatarGradient(r.fullName || "")}
        />
      ),
    },
    {
      key: "dateType",
      title: "Date & Type",
      label: "Date & Type",
      required: true,
      render: (_, r) => (
        <div className="whitespace-nowrap">
          <div className="flex items-center text-sm font-medium text-[var(--color-text-primary)]">
            <Calendar className="w-4 h-4 mr-1 text-[var(--color-text-tertiary)] shrink-0" />
            {formatDate(r.date)}
          </div>
          <div className="text-xs text-[var(--color-text-tertiary)] mt-0.5">
            {r.regularizationTypeName}
          </div>
        </div>
      ),
    },
    {
      key: "requestedTime",
      title: "Requested Time",
      label: "Requested Time",
      render: (_, r) => (
        <div className="text-sm text-[var(--color-text-primary)] flex items-center whitespace-nowrap">
          <Clock className="w-4 h-4 mr-1 text-[var(--color-text-tertiary)] shrink-0" />
          {formatTime(r.requestedCheckInTime)} - {formatTime(r.requestedCheckOutTime)}
        </div>
      ),
    },
    {
      key: "actualTime",
      title: "Actual Time",
      label: "Actual Time",
      render: (_, r) => (
        <div className="text-sm text-[var(--color-text-primary)] flex items-center whitespace-nowrap">
          <Clock className="w-4 h-4 mr-1 text-[var(--color-text-tertiary)] shrink-0" />
          {formatTime(r.actualCheckInTime || "")} - {formatTime(r.actualCheckOutTime || "")}
        </div>
      ),
    },
    {
      key: "reason",
      title: "Reason",
      label: "Reason",
      render: (_, r) => (
        <SimpleTooltip label={r.reason || "No reason provided"} side="top" tooltipClassName="max-w-xs text-xs">
          <span
            className="text-sm text-[var(--color-text-secondary)] block max-w-[220px] truncate cursor-default"
          >
            {r.reason || "-"}
          </span>
        </SimpleTooltip>
      ),
    },
    {
      key: "status",
      title: "Status",
      label: "Status",
      render: (_, r) => {
        const StatusIcon = getWorkflowStatusIcon(r.status);
        return (
          <Badge
            size="middle"
            variant={getWorkflowStatusVariant(r.status)}
            icon={
              isApprovedWorkflowStatus(r.status) ? (
                <CheckCircle className="w-4 h-4" />
              ) : (
                <StatusIcon className="w-4 h-4" />
              )
            }
          >
            {r.statusLabel ||
              getWorkflowStatusFallbackLabel(r.status, r.currentActorName)}
          </Badge>
        );
      },
    },
    {
      key: "appliedDate",
      title: "Applied Date",
      label: "Applied Date",
      render: (_, r) => (
        <div className="text-sm text-[var(--color-text-tertiary)] whitespace-nowrap">
          {formatISODate(r.appliedDate)}
        </div>
      ),
    },
    {
      key: "actions",
      title: "Actions",
      label: "Actions",
      align: "right",
      required: true,
      render: (_, r) => (
        <div className="flex items-center justify-end gap-1.5 whitespace-nowrap">
          <SimpleTooltip label="View Details" side="top" tooltipClassName="text-xs shadow-md border-0">
            <IconActionButton title="View Details" onClick={() => handleViewDetails(r)}>
              <Eye className="w-4 h-4" />
            </IconActionButton>
          </SimpleTooltip>

          {canActOnRequest(r) && (
            <>
              <SimpleTooltip label="Approve" side="top" tooltipClassName="text-xs shadow-md border-0">
                <ApproveButton
                  onApprove={() => handleApproveClick(r)}
                  loading={processing === r.id}
                  iconOnly
                  size="sm"
                  label="Approve"
                />
              </SimpleTooltip>
              <SimpleTooltip label="Reject" side="top" tooltipClassName="text-xs shadow-md border-0">
                <RejectButton
                  onReject={() => handleRejectClick(r)}
                  loading={processing === r.id}
                  iconOnly
                  size="sm"
                  label="Reject"
                />
              </SimpleTooltip>
            </>
          )}
        </div>
      ),
    },
  ];
};

export const getHistoryColumns = (
  config: BaseColumnConfig,
): TableColumn<IRegularizationRequest>[] => {
  const {
    formatDate,
    formatTime,
    handleViewDetails,
  } = config;

  return [
    {
      key: "employee",
      title: "Employee",
      label: "Employee",
      required: true,
      render: (_, r) => (
        <EmployeeCell
          name={r.fullName || "-"}
          sub={`${r.employeeId || ""} • ${r.departmentName || r.department || ""}`}
          avatarBg={getAvatarGradient(r.fullName || "")}
        />
      ),
    },
    {
      key: "dateType",
      title: "Date & Type",
      label: "Date & Type",
      required: true,
      render: (_, r) => (
        <div className="whitespace-nowrap">
          <div className="flex items-center text-sm font-medium text-[var(--color-text-primary)]">
            <Calendar className="w-4 h-4 mr-1 text-[var(--color-text-tertiary)] shrink-0" />
            {formatDate(r.date)}
          </div>
          <div className="text-xs text-[var(--color-text-tertiary)] mt-0.5">
            {r.regularizationTypeName}
          </div>
        </div>
      ),
    },
    {
      key: "requestedTime",
      title: "Requested Time",
      label: "Requested Time",
      render: (_, r) => (
        <div className="text-sm text-[var(--color-text-primary)] flex items-center whitespace-nowrap">
          <Clock className="w-4 h-4 mr-1 text-[var(--color-text-tertiary)] shrink-0" />
          {formatTime(r.requestedCheckInTime)} - {formatTime(r.requestedCheckOutTime)}
        </div>
      ),
    },
    {
      key: "actualTime",
      title: "Actual Time",
      label: "Actual Time",
      render: (_, r) => (
        <div className="text-sm text-[var(--color-text-primary)] flex items-center whitespace-nowrap">
          <Clock className="w-4 h-4 mr-1 text-[var(--color-text-tertiary)] shrink-0" />
          {formatTime(r.actualCheckInTime || "")} - {formatTime(r.actualCheckOutTime || "")}
        </div>
      ),
    },
    {
      key: "reason",
      title: "Reason",
      label: "Reason",
      render: (_, r) => (
        <span
          className="text-sm text-[var(--color-text-secondary)] block max-w-[220px] truncate"
        >
          {r.reason || "-"}
        </span>
      ),
    },
    {
      key: "status",
      title: "Status",
      label: "Status",
      render: (_, r) => {
        const Icon = getWorkflowStatusIcon(r.status);
        return (
          <Badge size="middle" variant={getWorkflowStatusVariant(r.status)}>
            <Icon className="w-4 h-4" />
            {r.statusLabel ||
              getWorkflowStatusFallbackLabel(r.status, r.currentActorName)}
          </Badge>
        );
      },
    },
    {
      key: "appliedDate",
      title: "Applied Date",
      label: "Applied Date",
      render: (_, r) => (
        <div className="text-sm text-[var(--color-text-tertiary)] whitespace-nowrap">
          {formatISODate(r.appliedDate)}
        </div>
      ),
    },
    {
      key: "actions",
      title: "Actions",
      label: "Actions",
      align: "right",
      required: true,
      render: (_, r) => (
        <div className="flex items-center justify-end whitespace-nowrap">
          <SimpleTooltip label="View Details" side="top" tooltipClassName="text-xs shadow-md border-0">
            <IconActionButton title="View Details" onClick={() => handleViewDetails(r)}>
              <Eye className="w-4 h-4" />
            </IconActionButton>
          </SimpleTooltip>
        </div>
      ),
    },
  ];
};
